import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormTextField } from '@/components/ui/form-text-field';
import { FormDatePicker } from '@/components/ui/form-date-picker';
import { FormTextareaField } from '@/components/ui/form-textarea-field';
import { eventFormClasses } from '@/lib/utils/events.utils';
import type { GuestFormInstance } from '@/lib/hooks/useGuestForm';
import { RegisterGuestContent } from '@/content/RegisterGuestContent';

type GuestDetailsSectionProps = {
  form: GuestFormInstance;
  submitAttempted?: boolean;
  validationErrors?: Record<string, string[]>;
};

/**
 * Guest details section of the guest registration form
 */
export function GuestDetailsSection({
  form,
  submitAttempted = false,
  validationErrors = {},
}: GuestDetailsSectionProps) {
  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitle>Guest Information</CardTitle>
        <div className="h-[1px] bg-[#F9F8F8]/15 border-0" />
      </CardHeader>

      <CardContent className="space-y-6">
        <form.Field
          name="firstName"
          children={(field) => (
            <FormTextField
              field={field}
              label={RegisterGuestContent.firstName}
              placeholder={RegisterGuestContent.firstNamePlaceholder}
              autoComplete="given-name"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.firstName}
            />
          )}
        />

        <form.Field
          name="lastName"
          children={(field) => (
            <FormTextField
              field={field}
              label={RegisterGuestContent.lastName}
              placeholder={RegisterGuestContent.lastNamePlaceholder}
              autoComplete="family-name"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.lastName}
            />
          )}
        />

        <form.Field
          name="email"
          children={(field) => (
            <FormTextField
              field={field}
              label={RegisterGuestContent.email}
              placeholder={RegisterGuestContent.emailPlaceholder}
              type="email"
              autoComplete="email"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.email}
            />
          )}
        />

        <form.Field
          name="visitDate"
          children={(field) => (
            <FormDatePicker
              id={field.name}
              name={field.name}
              label={RegisterGuestContent.visitDate}
              value={field.state.value}
              placeholder={RegisterGuestContent.visitDatePlaceholder}
              errorText={validationErrors.visitDate || field.state.meta.errors}
              forceShowErrors={submitAttempted}
              isTouched={field.state.meta.isTouched}
              onChange={(value) => field.handleChange(value ?? '')}
              onBlur={field.handleBlur}
            />
          )}
        />

        <form.Field
          name="reasonForVisit"
          children={(field) => (
            <FormTextareaField
              field={field}
              label={RegisterGuestContent.reasonForVisit}
              placeholder={RegisterGuestContent.reasonForVisitPlaceholder}
              maxLength={500}
              helperText="Brief description of the visit purpose"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.reasonForVisit}
            />
          )}
        />

        <form.Field
          name="acknowledgment"
          children={(field) => {
            const errors =
              validationErrors.acknowledgment || field.state.meta.errors || [];
            const hasErrors = errors.length > 0;
            const showErrors = submitAttempted || field.state.meta.isTouched;
            const isInvalid = showErrors && hasErrors;

            return (
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    onBlur={field.handleBlur}
                    aria-invalid={isInvalid || undefined}
                    className={`w-5 h-5 bg-slate-600 border border-slate-400 rounded text-purple-400 focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-slate-700 cursor-pointer ${isInvalid ? 'border-red-500' : ''}`}
                  />
                  <span className={`text-small ${eventFormClasses.label}`}>
                    {RegisterGuestContent.acknowledgment}
                  </span>
                </label>
                {showErrors && hasErrors && (
                  <div className="text-small text-red-500">{errors[0]}</div>
                )}
              </div>
            );
          }}
        />
      </CardContent>
    </Card>
  );
}

